import React, { useState, useRef } from 'react';
import { X, Save, Building2, Database, Globe, User, Key, Server, Upload, Image, FileText } from 'lucide-react';
import { collection, addDoc } from 'firebase/firestore';
import { db, storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import api from '../../utils/api';

interface CreateClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    database: {
      host: '',
      user: '',
      database: '',
      password: '',
      port: 3306
    },
    logo: '',
    logoHeight: 50,
    active: true,
    client_type: 'standard',
    catalogadoPrompt: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Por favor seleccione un archivo de imagen válido');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 5MB');
      return;
    }

    setUploadingLogo(true);
    setError(null);

    try {
      const timestamp = Date.now();
      const fileName = `client-logos/${timestamp}-${file.name}`;
      
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setFormData(prev => ({ ...prev, logo: downloadURL }));
      
    } catch (err: any) {
      console.error('Error uploading logo:', err);
      setError('Error al subir el logo. Por favor intente nuevamente.');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.name) {
        setError('Por favor ingrese un nombre para el cliente');
        setLoading(false);
        return;
      }

      if (!formData.database.host || !formData.database.user || !formData.database.database || !formData.database.password) {
        setError('Por favor complete todos los campos de la base de datos');
        setLoading(false);
        return;
      }

      // Let Firebase generate the ID automatically
      const clientsCollection = collection(db, 'clients');

      const newDocRef = await addDoc(clientsCollection, {
        name: formData.name,
        display_name: formData.display_name || formData.name,
        database: formData.database,
        logo: formData.logo,
        logoHeight: formData.logoHeight,
        active: formData.active,
        client_type: formData.client_type,
        catalogadoPrompt: formData.catalogadoPrompt,
        created_at: new Date(),
        created_by: 'admin'
      });

      // Inicializar columnConfig.core con defaults (no fatal si falla)
      try {
        await api.post(`/api/clients/${newDocRef.id}/seed-core`);
      } catch (seedErr: any) {
        console.warn(`seedCoreForClient failed for ${newDocRef.id}:`, seedErr?.message || seedErr);
      }

      onSave();
      onClose();
      
      setFormData({
        name: '',
        display_name: '',
        database: {
          host: '',
          user: '',
          database: '',
          password: '',
          port: 3306
        },
        logo: '',
        logoHeight: 50,
        active: true,
        client_type: 'standard',
        catalogadoPrompt: ''
      });
    } catch (err: any) {
      setError(err.message || 'Error al crear el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Crear Nuevo Cliente
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Información Básica
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Cliente *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ej: Cliente ABC"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre para Mostrar
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ej: Cliente ABC S.A."
                />
              </div>
            </div>

            <div className="space-y-4">
              {formData.logo && (
                <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={formData.logo} 
                    alt="Logo preview" 
                    style={{ height: `${formData.logoHeight}px`, maxHeight: '60px' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              )}
              
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {uploadingLogo ? (
                      <>
                        <Upload className="w-4 h-4 animate-spin" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Subir Logo
                      </>
                    )}
                  </button>
                  
                  {formData.logo && (
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, logo: '' }))}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Quitar Logo
                    </button>
                  )}
                </div>

                <div className="text-sm text-gray-500">
                  <p>• Formatos aceptados: JPG, PNG, SVG, GIF</p>
                  <p>• Tamaño máximo: 5MB</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Image className="w-4 h-4 inline mr-1" />
                    URL del Logo (opcional)
                  </label>
                  <input
                    type="url"
                    value={formData.logo || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, logo: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://example.com/logo.png"
                    disabled={uploadingLogo}
                  />
                </div>
              
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Altura del Logo (px)
                  </label>
                  <input
                    type="number"
                    value={formData.logoHeight}
                    onChange={(e) => setFormData({ ...formData, logoHeight: parseInt(e.target.value) || 50 })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="20"
                    max="100"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Configuración de Base de Datos
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Server className="w-4 h-4 inline mr-1" />
                  Host *
                </label>
                <input
                  type="text"
                  value={formData.database.host}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    database: { ...formData.database, host: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ej: localhost, 192.168.1.100"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Puerto
                </label>
                <input
                  type="number"
                  value={formData.database.port}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    database: { ...formData.database, port: parseInt(e.target.value) }
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="3306"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Database className="w-4 h-4 inline mr-1" />
                  Nombre de Base de Datos *
                </label>
                <input
                  type="text"
                  value={formData.database.database}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    database: { ...formData.database, database: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ej: catalog, tracking_db"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Usuario *
                </label>
                <input
                  type="text"
                  value={formData.database.user}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    database: { ...formData.database, user: e.target.value }
                  })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ej: root, admin"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <Key className="w-4 h-4 inline mr-1" />
                Contraseña *
              </label>
              <input
                type="password"
                value={formData.database.password}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  database: { ...formData.database, password: e.target.value }
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          {/* Prompt de Catalogado */}
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Prompt de Catalogado
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instrucciones de Catalogado Principal
              </label>
              <textarea
                value={formData.catalogadoPrompt}
                onChange={(e) => setFormData({ ...formData, catalogadoPrompt: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={6}
                placeholder="Ingrese las instrucciones principales de catalogado para este cliente. Estas serán la base para el procesamiento de productos, considerando luego las reglas específicas de categorías y marcas."
              />
              <p className="text-sm text-gray-500 mt-1">
                Este prompt será utilizado como base para el catalogado de productos. Se complementará con las reglas específicas de categorías y marcas.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="active" className="text-sm font-medium text-gray-700">
                Cliente Activo
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Cliente
              </label>
              <select
                value={formData.client_type}
                onChange={(e) => setFormData({ ...formData, client_type: e.target.value })}
                className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="standard">Estándar</option>
                <option value="premium">Premium</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Creando...' : 'Crear Cliente'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateClientModal;